import React from 'react';
import { Box, Typography } from '@mui/material';
import type { Cutsheet } from 'lib/graphql';

/**
 * Interface for cutsheet data structure
 */
interface CutsheetTooltipData {
  name: string;
  primalCuts?: Array<{
    _id: string;
    name?: string;
    title?: string;
    primal?: {
      name: string;
    } | null;
  }>;
  selectedSpecifications?: Record<string, any>;
  extras?: any;
  trim?: Record<string, any>;
  notes?: string;
}

interface CutsheetTooltipContentProps {
  cutsheet: Cutsheet | CutsheetTooltipData;
  /** Optional notes to display, overrides cutsheet.notes */
  notes?: string | null;
  /** Whether to show the cutsheet name header. Defaults to true. */
  showName?: boolean;
}

/**
 * Reusable component for displaying cutsheet details in a tooltip or popover.
 * Groups primal cuts by their primal parent for better organization.
 * @param root0
 * @param root0.cutsheet
 * @param root0.notes
 */
export function CutsheetTooltipContent({
  cutsheet,
  notes,
  showName = true,
}: CutsheetTooltipContentProps) {
  // Group primal cuts by their primal name
  const groupedPrimalCuts = React.useMemo(() => {
    if (!cutsheet.primalCuts || cutsheet.primalCuts.length === 0) {
      return {};
    }

    return cutsheet.primalCuts.reduce(
      (acc, cut: any) => {
        const primalName = cut.primal?.name || 'Uncategorized';
        const cutName = cut.name || cut.title || 'Unnamed Cut';

        if (!acc[primalName]) {
          acc[primalName] = [];
        }
        acc[primalName].push(cutName);

        return acc;
      },
      {} as Record<string, string[]>,
    );
  }, [cutsheet.primalCuts]);

  return (
    <Box sx={{ maxWidth: 400, p: 1 }}>
      {showName && (
        <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
          {cutsheet.name}
        </Typography>
      )}

      {/* Primal Cuts - Grouped by Primal */}
      {Object.keys(groupedPrimalCuts).length > 0 && (
        <Box sx={{ mb: 2 }}>
          <Typography
            variant="caption"
            sx={{
              fontWeight: 600,
              color: 'primary.main',
              display: 'block',
              mb: 0.5,
            }}
          >
            Primal Cuts:
          </Typography>
          <Box sx={{ ml: 1 }}>
            {Object.entries(groupedPrimalCuts).map(([primalName, cuts]) => (
              <Box key={primalName} sx={{ mb: 0.5 }}>
                <Typography
                  variant="body2"
                  sx={{ fontSize: '1rem', fontWeight: 500 }}
                >
                  {primalName}:{' '}
                  <Typography
                    component="span"
                    variant="body2"
                    sx={{ fontSize: '1rem', fontWeight: 400 }}
                  >
                    {cuts.join(', ')}
                  </Typography>
                </Typography>
              </Box>
            ))}
          </Box>
        </Box>
      )}

      {/* Specifications */}
      {cutsheet.selectedSpecifications &&
        Object.keys(cutsheet.selectedSpecifications).length > 0 && (
          <Box sx={{ mb: 2 }}>
            <Typography
              variant="caption"
              sx={{
                fontWeight: 600,
                color: 'secondary.main',
              }}
            >
              Specifications:
            </Typography>
            <Box sx={{ ml: 1 }}>
              {Object.entries(cutsheet.selectedSpecifications)
                .filter(
                  ([_, value]) =>
                    value !== null && value !== undefined && value !== '',
                )
                .map(([key, value]) => {
                  const formattedKey = key
                    .replace(/([A-Z])/g, ' $1')
                    .replace(/^./, (str) => str.toUpperCase());

                  // Handle different value types
                  let displayValue;
                  if (typeof value === 'object' && value !== null) {
                    const valueObj = value as any;
                    if (valueObj.label || valueObj.name) {
                      displayValue = valueObj.label || valueObj.name;
                    } else if (valueObj.value !== undefined) {
                      displayValue = valueObj.value;
                    } else {
                      displayValue = JSON.stringify(value);
                    }
                  } else {
                    displayValue = value;
                  }

                  return (
                    <Typography
                      key={key}
                      variant="body2"
                      sx={{
                        fontSize: '1rem',
                        display: 'block',
                        mb: 0.5,
                      }}
                    >
                      • {formattedKey}: {displayValue}
                    </Typography>
                  );
                })}
              {Object.entries(cutsheet.selectedSpecifications).filter(
                ([_, value]) =>
                  value !== null && value !== undefined && value !== '',
              ).length === 0 && (
                <Typography variant="body2" sx={{ fontSize: '1rem' }}>
                  None
                </Typography>
              )}
            </Box>
          </Box>
        )}

      {/* Extras */}
      {cutsheet.extras && (
        <Box sx={{ mb: 1 }}>
          <Typography
            variant="caption"
            sx={{ fontWeight: 600, color: 'info.main' }}
          >
            Extras:
          </Typography>
          <Typography variant="body2" sx={{ fontSize: '1rem' }}>
            {Object.entries(cutsheet.extras)
              .filter(([_, value]) => value === true)
              .map(([key]) =>
                key
                  .replace(/([A-Z])/g, ' $1')
                  .replace(/^./, (str) => str.toUpperCase()),
              )
              .join(', ') || 'None'}
          </Typography>
        </Box>
      )}

      {/* Trim */}
      {cutsheet.trim && (
        <Box>
          <Typography
            variant="caption"
            sx={{
              fontWeight: 600,
              color: 'warning.main',
            }}
          >
            Trim:
          </Typography>
          <Typography variant="body2" sx={{ fontSize: '1rem' }}>
            {Object.entries(cutsheet.trim)
              .filter(
                ([_, value]) =>
                  value !== null && value !== undefined && value !== '',
              )
              .map(([key, value]) => {
                // Handle different value types
                let displayValue;
                if (typeof value === 'object' && value !== null) {
                  // If it's an object, try to extract meaningful info
                  if (value.label || value.name) {
                    displayValue = value.label || value.name;
                  } else if (value.value !== undefined) {
                    displayValue = value.value;
                  } else {
                    displayValue = JSON.stringify(value);
                  }
                } else {
                  displayValue = value;
                }

                const formattedKey = key
                  .replace(/([A-Z])/g, ' $1')
                  .replace(/^./, (str) => str.toUpperCase());

                return `${formattedKey}: ${displayValue}`;
              })
              .join(', ') || 'Default'}
          </Typography>
        </Box>
      )}

      {/* Notes */}
      {(notes || cutsheet.notes) && (
        <Box
          sx={{
            mt: 2,
            pt: 1,
            borderTop: '1px solid',
            borderColor: 'divider',
          }}
        >
          <Typography variant="caption" sx={{ fontWeight: 600 }}>
            Notes:
          </Typography>
          <Typography variant="body2" sx={{ fontSize: '1rem' }}>
            {typeof (notes || cutsheet.notes) === 'string'
              ? notes || cutsheet.notes
              : (notes || cutsheet.notes) &&
                  typeof (notes || cutsheet.notes) === 'object'
                ? JSON.stringify(notes || cutsheet.notes, null, 2)
                : String(notes || cutsheet.notes || '')}
          </Typography>
        </Box>
      )}
    </Box>
  );
}
